import React, { useState, useEffect, useRef } from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ScatterChart,
  Scatter
} from 'recharts';
import { ChartBarIcon, ChevronDownIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';

// Color schemes used in analytics dashboard
const COLOR_SCHEMES = {
  blue: ['#3B82F6', '#60A5FA', '#93C5FD', '#BFDBFE', '#DBEAFE'],
  green: ['#10B981', '#34D399', '#6EE7B7', '#9DECDA', '#C6F6E8'],
  purple: ['#8B5CF6', '#A78BFA', '#C4B5FD', '#DDD6FE', '#EDE9FE'],
  orange: ['#F59E0B', '#FBBF24', '#FCD34D', '#FDE68A', '#FEF3C7'],
  red: ['#EF4444', '#F87171', '#FCA5A5', '#FECACA', '#FEE2E2'],
  teal: ['#14B8A6', '#5EEAD4', '#99F6E4', '#CCFBF1', '#F0FDFA']
};

const CHART_TYPES = [
  { value: 'bar', label: 'Bar Chart' },
  { value: 'line', label: 'Line Chart' },
  { value: 'area', label: 'Area Chart' },
  { value: 'scatter', label: 'Scatter Plot' },
  { value: 'pie', label: 'Pie Chart' }
];

const EnhancedChartVisualization = ({ data, messageIdx }) => {
  const [showChart, setShowChart] = useState(false);
  const [chartType, setChartType] = useState('bar');
  const [selectedXAxis, setSelectedXAxis] = useState('');
  const [selectedYAxis, setSelectedYAxis] = useState('');
  const [colorScheme, setColorScheme] = useState('blue');
  const [maxRecords, setMaxRecords] = useState(20);
  
  // Ref for chart container to capture as image
  const chartRef = useRef(null);

  // Helper function to detect numeric columns
  const getNumericColumns = (data) => {
    if (!data || data.length === 0) return [];
    
    return Object.keys(data[0]).filter(key => {
      const values = data.slice(0, 10).map(item => item[key]);
      return values.some(val => !isNaN(parseFloat(val)) && isFinite(val));
    });
  };

  // Helper function to detect categorical columns
  const getCategoricalColumns = (data) => {
    if (!data || data.length === 0) return [];
    return Object.keys(data[0]);
  };

  // CSV download function
  const downloadCSV = (data, filename = 'chart_data.csv') => {
    if (!data || data.length === 0) {
      alert('No data to download');
      return;
    }

    const keys = Object.keys(data[0]);
    
    // Create CSV content
    const csvContent = [
      // Header row
      keys.join(','),
      // Data rows
      ...data.map(row => 
        keys.map(key => {
          const value = row[key];
          // Handle values with commas, quotes, or newlines
          if (value === null || value === undefined) {
            return '';
          }
          const stringValue = String(value);
          if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        }).join(',')
      )
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Function to download chart as PNG (using html2canvas or similar would be needed for full implementation)
  const downloadChart = (format = 'csv') => {
    if (format === 'csv') {
      // Download chart data as CSV
      const chartData = prepareChartData();
      downloadCSV(chartData, `chart_${chartType}_${Date.now()}.csv`);
    } else if (format === 'png') {
      // Download chart as PNG using canvas
      downloadChartAsPNG();
    }
  };

  // Function to download chart as PNG
  const downloadChartAsPNG = () => {
    if (!chartRef.current) {
      alert('Chart not ready for download');
      return;
    }

    // Use html2canvas to capture the chart
    import('html2canvas').then(html2canvas => {
      html2canvas.default(chartRef.current, {
        backgroundColor: '#ffffff',
        scale: 2, // Higher resolution
        useCORS: true,
        allowTaint: true
      }).then(canvas => {
        // Create download link
        const link = document.createElement('a');
        link.download = `chart_${chartType}_${Date.now()}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
      }).catch(error => {
        console.error('Error generating PNG:', error);
        alert('Failed to generate PNG. Please try CSV download instead.');
      });
    }).catch(error => {
      console.error('html2canvas not available:', error);
      alert('PNG download not available. Please use CSV download instead.');
    });
  };

  // Initialize axis selections
  useEffect(() => {
    if (!data || data.length === 0) return;
    
    const categoricalColumns = getCategoricalColumns(data);
    const numericColumns = getNumericColumns(data);
    
    if (categoricalColumns.length > 0 && !selectedXAxis) {
      setSelectedXAxis(categoricalColumns[0]);
    }
    if (numericColumns.length > 0 && !selectedYAxis) {
      setSelectedYAxis(numericColumns[0]);
    }
  }, [data, selectedXAxis, selectedYAxis]);

  // Prepare chart data
  const prepareChartData = () => {
    if (!data || data.length === 0) return [];
    
    const chartData = data.slice(0, maxRecords);
    
    if (chartType === 'pie') {
      // For pie charts, group by X-axis and sum Y-axis values
      const grouped = {};
      chartData.forEach(item => {
        const key = String(item[selectedXAxis] || 'Unknown');
        const value = parseFloat(item[selectedYAxis]) || 0;
        grouped[key] = (grouped[key] || 0) + value;
      });
      
      return Object.entries(grouped).map(([name, value]) => ({
        name,
        value,
        [selectedYAxis]: value
      }));
    }
    
    return chartData.map((item, index) => ({
      ...item,
      [selectedXAxis]: String(item[selectedXAxis] || `Item ${index + 1}`),
      [selectedYAxis]: parseFloat(item[selectedYAxis]) || 0
    }));
  };

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900">{`${selectedXAxis}: ${label}`}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {`${entry.dataKey}: ${typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Render chart based on type
  const renderChart = () => {
    const chartData = prepareChartData();
    const colors = COLOR_SCHEMES[colorScheme];
    
    if (chartData.length === 0) {
      return (
        <div className="flex items-center justify-center h-64 text-gray-500">
          <div className="text-center">
            <ChartBarIcon className="w-12 h-12 mx-auto mb-2 text-gray-400" />
            <p>No data available for visualization</p>
          </div>
        </div>
      );
    }

    const commonProps = {
      data: chartData,
      margin: { top: 20, right: 30, left: 20, bottom: 60 }
    };

    switch (chartType) {
      case 'bar':
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey={selectedXAxis} 
              angle={-45}
              textAnchor="end"
              height={80}
              fontSize={12}
              stroke="#6b7280"
            />
            <YAxis fontSize={12} stroke="#6b7280" />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar 
              dataKey={selectedYAxis} 
              fill={colors[0]}
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        );

      case 'line':
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey={selectedXAxis}
              angle={-45}
              textAnchor="end"
              height={80}
              fontSize={12}
              stroke="#6b7280"
            />
            <YAxis fontSize={12} stroke="#6b7280" />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line
              type="monotone"
              dataKey={selectedYAxis}
              stroke={colors[0]}
              strokeWidth={3}
              dot={{ fill: colors[0], strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, fill: colors[1] }}
            />
          </LineChart>
        );

      case 'area':
        return (
          <AreaChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey={selectedXAxis}
              angle={-45}
              textAnchor="end"
              height={80}
              fontSize={12}
              stroke="#6b7280"
            />
            <YAxis fontSize={12} stroke="#6b7280" />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Area
              type="monotone"
              dataKey={selectedYAxis}
              stroke={colors[0]}
              fill={colors[0]}
              fillOpacity={0.3}
              strokeWidth={2}
            />
          </AreaChart>
        );

      case 'scatter':
        return (
          <ScatterChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              type="category"
              dataKey={selectedXAxis}
              angle={-45}
              textAnchor="end"
              height={80}
              fontSize={12}
              stroke="#6b7280"
            />
            <YAxis 
              type="number"
              dataKey={selectedYAxis}
              fontSize={12}
              stroke="#6b7280"
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Scatter
              dataKey={selectedYAxis}
              fill={colors[0]}
            />
          </ScatterChart>
        );

      case 'pie':
        return (
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
              outerRadius={120}
              fill="#8884d8"
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend />
          </PieChart>
        );

      default:
        return null;
    }
  };

  if (!data || data.length === 0) {
    return (
      <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="text-sm text-gray-600 flex items-center gap-2">
          <ChartBarIcon className="w-4 h-4" />
          No data available for visualization
        </div>
      </div>
    );
  }

  const categoricalColumns = getCategoricalColumns(data);
  const numericColumns = getNumericColumns(data);

  if (numericColumns.length === 0) {
    return (
      <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="text-sm text-gray-600 flex items-center gap-2">
          <ChartBarIcon className="w-4 h-4" />
          No numeric columns available for visualization
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="font-semibold text-blue-800 flex items-center gap-2">
          <ChartBarIcon className="w-5 h-5" />
          Enhanced Data Visualization
        </div>
        <div className="flex items-center gap-2">
          {showChart && (
            <button
              onClick={() => downloadChart('png')}
              className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors flex items-center gap-1"
              title="Download chart as PNG"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              Download
            </button>
          )}
          <button
            onClick={() => setShowChart(!showChart)}
            className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1"
          >
            {showChart ? 'Hide Chart' : 'Show Chart'}
            <ChevronDownIcon className={`w-4 h-4 transition-transform ${showChart ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>

      {showChart && (
        <div className="space-y-4">
          {/* Chart Controls */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 p-4 bg-white rounded-lg border border-gray-200">
            {/* Chart Type */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Chart Type
              </label>
              <select
                value={chartType}
                onChange={(e) => setChartType(e.target.value)}
                className="w-full text-xs border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {CHART_TYPES.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* X-Axis */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {chartType === 'pie' ? 'Category' : 'X-Axis'}
              </label>
              <select
                value={selectedXAxis}
                onChange={(e) => setSelectedXAxis(e.target.value)}
                className="w-full text-xs border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {categoricalColumns.map(col => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>

            {/* Y-Axis */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {chartType === 'pie' ? 'Value' : 'Y-Axis'}
              </label>
              <select
                value={selectedYAxis}
                onChange={(e) => setSelectedYAxis(e.target.value)}
                className="w-full text-xs border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {numericColumns.map(col => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>

            {/* Color Scheme */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Color Scheme
              </label>
              <select
                value={colorScheme}
                onChange={(e) => setColorScheme(e.target.value)}
                className="w-full text-xs border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {Object.keys(COLOR_SCHEMES).map(scheme => (
                  <option key={scheme} value={scheme}>
                    {scheme.charAt(0).toUpperCase() + scheme.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {/* Max Records */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Max Records
              </label>
              <select
                value={maxRecords}
                onChange={(e) => setMaxRecords(parseInt(e.target.value))}
                className="w-full text-xs border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            {/* Data Info */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Data Info
              </label>
              <div className="text-xs text-gray-600 py-1.5">
                {data.length} total records
              </div>
            </div>
          </div>

          {/* Chart Container */}
          <div ref={chartRef} className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                {renderChart()}
              </ResponsiveContainer>
            </div>
          </div>

          {/* Chart Info */}
          <div className="text-xs text-gray-600 bg-white rounded-lg border border-gray-200 p-3">
            <div className="flex flex-wrap gap-4">
              <span>Showing {Math.min(maxRecords, data.length)} of {data.length} records</span>
              <span>Chart: {CHART_TYPES.find(t => t.value === chartType)?.label}</span>
              <span>Theme: {colorScheme.charAt(0).toUpperCase() + colorScheme.slice(1)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedChartVisualization;
